// The shared contracts of the full-page table. Every module codes against these shapes and talks to the rest
// of the table through TableContext (context.js). A module that needs a field proposes it here rather than
// reading another module's internals.

// ---- configuration ----

/**
 * @typedef {'text'|'numeric'|'date'|'badge'|'link'|'button'|'split'|'actions'} ColumnType
 * @typedef {'ascending'|'descending'} SortDirection
 * @typedef {{key: string, direction: SortDirection}} Sort
 * @typedef {'start'|'end'|null} LockEdge
 * @typedef {'nowrap'|'ellipsis'|'wrap'} Wrap
 * @typedef {'comfortable'|'compact'} Density
 */

/**
 * What a column takes part in. A null value means the column does not take part.
 * @typedef {object} ColumnCapabilities
 * @property {boolean} search
 * @property {'enum'|'numeric'|'date'|null} filter
 * @property {boolean} sort
 * @property {'sum'|((matching: object[]) => *)|null} summary a named aggregate, or a function of the complete
 *   matching set
 * @property {((row: object) => *)|null} export the plain value an export writes
 */

/**
 * An enumerable filter's refined option: while it is the selected option, rows also pass `test`.
 * @typedef {{option: string, label: string, test: (row: object) => boolean}} Refinement
 */

/**
 * Every field below is present after normalization (config/columns.js normalizeColumn); declared fields the
 * normalizer does not touch (format, value, searchText, render, sortOrder, options, actions, actionsLabel,
 * exportValue, exportable, className) pass through unchanged.
 * @typedef {object} NormalizedColumn
 * @property {string} key
 * @property {string} label '' for an actions column
 * @property {ColumnType} type
 * @property {string} [format] 'currency' for money
 * @property {boolean} required the first required column is the row identity; never true on actions
 * @property {ColumnCapabilities} capabilities
 * @property {LockEdge} lock an actions column always locks to 'end'
 * @property {boolean} visible required and actions columns are always visible
 * @property {boolean} hideable default: true when unlocked; never for required or actions
 * @property {boolean} reorderable default true; never for required, locked or actions
 * @property {boolean} resizable default true; never for actions
 * @property {{width: number, min: number, max: number}} size pixels, defaults by type (config/defaults.js)
 * @property {Wrap} wrap default 'wrap'
 * @property {string} [className]
 * @property {string[]} [options] declared enumerable filter options
 * @property {?Refinement} refinement
 * @property {Array<*>} [sortOrder]
 * @property {number} minMenuWidth pixels, for link, split and actions menus; defaults to the instance's
 * @property {(row: object) => *} [value] the plain value behind a custom cell
 * @property {(row: object) => string} [searchText]
 * @property {(row: object) => *} [render]
 */

/**
 * A command runs `onSelect`; a destination is `href`. One of them is declared.
 * @typedef {{label: string, onSelect?: Function, href?: string, icon?: string}} Trigger
 */

/**
 * @typedef {object} PageAction
 * @property {string} key
 * @property {string} label
 * @property {Function} [onSelect]
 * @property {string} [href]
 * @property {string} [icon]
 * @property {'primary'|'secondary'} treatment default 'secondary'
 * @property {boolean} destructive
 * @property {boolean} [disabled]
 */

/**
 * @typedef {object} NormalizedConfig
 * @property {string} tableId
 * @property {boolean} embedded default false; section named by caption, input order and bottom paging only
 * @property {string} [title] unused in embedded mode
 * @property {1|2|3|4} headingLevel default 2
 * @property {string} caption
 * @property {string} [description]
 * @property {string} rowKey
 * @property {NormalizedColumn[]} columns
 * @property {Sort} defaultSort
 * @property {boolean} paged default true; false shows every row with no pager or count (embedded, client only)
 * @property {number[]} pageSizes
 * @property {number} defaultPageSize
 * @property {{mode: 'client'|'page', max?: number}} source a client source declares max
 * @property {string} [searchLabel]
 * @property {string} [rowActionsLabel] a visible row-actions trigger label in place of the kebab
 * @property {string} [searchPlaceholder]
 * @property {{title: string, description?: string, action?: Trigger|*}} emptyState `action` is a Trigger or
 *   a consumer-composed element
 * @property {{label: string}|null} summary
 * @property {?{bulkActions: Array<object>, isEligible: (row: object) => boolean, ineligibleReason: (row: object) => ?string}} selection
 * @property {?{key?: string, groupOf: (row: object) => *, groups: Array<{key: string, title: string, emptyText: string, expanded: boolean}>, renderHeader?: (group: object, api: {rows: object[], count: number, selected: number, selectable: boolean, select: () => void, clear: () => void}) => *}} grouped
 *   `renderHeader` returns the consumer's group-header content (counts, totals, color, actions)
 * @property {PageAction[]} pageActions empty when none are declared
 * @property {?number} minTableWidth pixels
 * @property {number} minMenuWidth pixels, default 160
 * @property {Density} defaultDensity default 'comfortable'
 */

// ---- state ----

/**
 * One enum filter: the chosen options, plus `refined: true` while the refinement is on.
 * @typedef {{selected: string[], refined?: true}} EnumFilterValue
 * @typedef {{mode: 'equal'|'greaterThan'|'lessThan'|'between', from: ?number, to: ?number}} NumericFilterValue
 * @typedef {{mode: 'on'|'before'|'after'|'between', from: ?string, to: ?string}} DateFilterValue
 * @typedef {string[]|EnumFilterValue|NumericFilterValue|DateFilterValue} FilterValue
 */

/**
 * @typedef {object} QueryState
 * @property {string} search the committed, trimmed search
 * @property {Object<string, FilterValue>} filters by column key; an empty value is inactive
 * @property {Sort} sort
 */

/**
 * @typedef {object} ViewState
 * @property {Density} density
 * @property {string[]} order column keys, display order
 * @property {string[]} hidden column keys
 * @property {Object<string, number>} widths pixels, by column key
 */

/**
 * @typedef {object} SelectionState
 * @property {'none'|'rows'|'page'|'all'} scope
 * @property {Set<string>} ids selected row identities ('rows' and 'page')
 * @property {Set<string>} excluded identities cleared out of an 'all' scope
 */

/**
 * @typedef {object} TableState
 * @property {string} searchDraft what the search box shows; committed into query.search
 * @property {QueryState} query
 * @property {number} page 1-based
 * @property {number} pageSize
 * @property {Set<string>} expanded row identities
 * @property {ViewState} view
 * @property {SelectionState} selection
 * @property {{sort: Sort, refinements: Object<string, string>}} defaults what reconciling the query needs from
 *   the configuration: the default sort and each refinable filter's refined option, by column key
 * @property {?ActionReason} reason the reason of the action that made this state; null initially
 */

// ---- actions ----

/**
 * Why a state change happened. Published with every state change so a consumer can tell a user's
 * interaction from a response or a controlled update.
 * @typedef {'search'|'filter'|'sort'|'page'|'pageSize'|'reset'|'expansion'|'view'|'selection'|'response'|'controlled'} ActionReason
 */
export const ACTION_REASONS = [
  'search',
  'filter',
  'sort',
  'page',
  'pageSize',
  'reset',
  'expansion',
  'view',
  'selection',
  'response',
  'controlled',
]

/**
 * Every action carries a type and a reason; the remaining fields depend on the type.
 * @typedef {object} Action
 * @property {string} type one of ACTIONS
 * @property {ActionReason} reason
 */
export const ACTIONS = {
  searchDraft: 'search/draft', // {value}
  searchCommit: 'search/commit', // {value}
  filterSet: 'filter/set', // {key, value}
  queryClear: 'query/clear',
  sortToggle: 'sort/toggle', // {key, defaultSort}
  pageSet: 'page/set', // {page}
  pageClamp: 'page/clamp', // {page}
  pageSizeSet: 'pageSize/set', // {size}
  rowToggle: 'row/toggle', // {id}
  expansionClear: 'expansion/clear',
  viewSet: 'view/set', // {view: Partial<ViewState>}
  selectionSet: 'selection/set', // {selection: Partial<SelectionState>}
  controlledMerge: 'controlled/merge', // {state: Partial<TableState>}
}

/**
 * What onStateChange(state, event) receives: one complete, JSON-serializable replacement state, published
 * synchronously when a change commits. Drafts, expansion and controlled updates publish nothing. The same
 * shape, possibly partial, is what the `controlledState` prop accepts.
 * @typedef {object} PublishedState
 * @property {string} tableId
 * @property {string[]} columns visible column keys, display order
 * @property {string[]} hidden hidden column keys, so a restore can tell them from columns added later
 * @property {Object<string, number>} widths pixels by column key, hidden columns included
 * @property {Density} density
 * @property {string} search
 * @property {Object<string, string[]|EnumFilterValue|NumericFilterValue|DateFilterValue>} filters
 * @property {Sort} sort
 * @property {number} page
 * @property {number} pageSize
 * @property {{scope: 'none'}|{scope: 'rows'|'page', ids: string[]}|{scope: 'all', query: {search: string, filters: object}, eligibleTotal: ?number, excluded: string[]}} [selection]
 *   present when selection is configured
 */

/**
 * @typedef {{reason: ?ActionReason, change: ?('query'|'page'|'pageSize'|'view'|'selection'|'expansion'|'response'|'controlled')}} StateChangeEvent
 */

/**
 * The control that started a request. Set when a person starts a request from a control (a page-backed
 * query, page or page-size change, or Retry in either mode) and kept until that request settles; when it
 * settles focus returns to `element` if it is still usable, else to the state heading that replaced the rows
 * (states/focusTarget.js). A request the person did not start leaves it null.
 * @typedef {{element: ?HTMLElement, userInitiated: boolean}} Initiator
 */

// ---- the context value ----

/**
 * @typedef {object} Derived what the source (source/client.js or source/page.js) produced
 * @property {object[]} data the loaded set: the complete client set, or the active page; empty unless ready
 * @property {object[]} matching client: the complete set after search and filters, sorted; page: the page
 * @property {object[]} pageRows the visible page
 * @property {number} total exact matching rows; 0 until a load succeeds
 * @property {?number} eligibleTotal exact matching rows selection may use; null until a load succeeds
 * @property {number} currentPage page, clamped once the matching count is known
 * @property {number} firstShown 1-based, 0 when nothing matches
 * @property {number} lastShown
 * @property {Object<string, *>} totals summary values by column key, over the complete matching set (a page
 *   source's response aggregates)
 * @property {boolean} hasQuery
 * @property {boolean} busy
 * @property {boolean} zeroData
 * @property {boolean} zeroResults
 */

/**
 * @typedef {object} Layout
 * @property {NormalizedColumn[]} columns the rendered columns, display order
 * @property {NormalizedColumn} idColumn
 * @property {number} columnCount every rendered column, control columns included
 * @property {boolean} expandable
 * @property {boolean} hasActions
 * @property {boolean} selectable
 * @property {boolean} searchable
 * @property {NormalizedColumn[]} filterColumns
 * @property {boolean} showSummary
 * @property {boolean} narrow below 768px (useBreakpoint)
 */

/**
 * @typedef {object} Geometry
 * @property {boolean} pinned the header is pinned below the application header
 * @property {number} stickyTop pixels from the scroll container's top
 * @property {(columnKey: string) => (object|undefined)} lockStyle inline style for a locked cell
 * @property {(columnKey: string) => {start: boolean, end: boolean}} lockEdges which locked edge a cell sits on
 * @property {(columnKey: string) => ?LockEdge} lockSide the side a column locks to while the table locks; null
 *   while it fits or for an unlocked column
 * @property {boolean} locking the table overflows its region at 768px and wider, so columns lock and the proxy
 *   scrollbar drives horizontal scrolling
 * @property {number} scrollWidth the table's layout width without the spare width truncated columns take, pixels
 * @property {number} visibleWidth the region's client width, pixels
 * @property {number} [contentWidth] the region's content width (its client width less its padding), pixels;
 *   the colgroup gives what the columns leave of it to truncated columns first (ColGroup.js spareOf)
 * @property {boolean} proxyShown the region is in view, so the proxy scrollbar shows
 * @property {{current: ?HTMLElement}} proxyRef the proxy scrollbar's element
 * @property {(columns: NormalizedColumn[], widths: Array<{key: ?string, width: number}>) => void} syncColumns
 *   registers the rendered columns, display order, and their rendered widths (control columns keyed null)
 *   before any cell reads its lock; locks that do not fit release for that render (locks.js releasedLocks)
 * @property {(total: number) => void} [syncSpare] registers the spare width the colgroup handed out this render
 * @property {(left: number) => void} scrollTo scrolls the table to a horizontal position, clamped
 * @property {() => void} invalidate re-measure after a width, order or visibility change
 * @property {Object<string, number>} [floors] each data column's measured content floor by key, its header's
 *   longest label word included (geometry/contentFloor.js); a column without one keeps its configured minimum
 * @property {Object<string, number>} [naturals] each data column's natural width by key: its whole header label
 *   or its widest truncating value, with the cell's padding
 * @property {number} proxyHeight pixels the proxy scrollbar takes at the bottom of the viewport
 * @property {number} toolbarFreeWidth pixels the filter group may take, More filters included, with search at
 *   its minimum width; 0 until the toolbar has been measured
 */

/**
 * @typedef {object} Slots components placed by the shell; a module renders a slot rather than importing it
 * @property {Function} ConsumerRegion
 * @property {Function} FilterBar
 * @property {Function} ViewOptions
 * @property {Function} TopPager
 * @property {Function} HeaderCheckbox
 * @property {Function} SelectCell
 * @property {Function} ResizeBoundary rendered inside each header cell, beside its sort control: ({column}); with
 *   lead, rendered in the next header cell as the leading half of that column's touch grab area
 * @property {Function} ColGroup
 * @property {Function} ProxyScrollbar
 * @property {Function} BulkBar
 * @property {Function} RowActions ({row, identity})
 * @property {Function} Cell ({column, row})
 */

/**
 * @typedef {object} TableContextValue
 * @property {NormalizedConfig} config
 * @property {'loading'|'error'|'ready'} status the consumer's for a client source, the request's for a page one
 * @property {TableState} state
 * @property {(action: Action) => void} dispatch applies and publishes; a page-backed request records its
 *   initiator
 * @property {Derived} derived
 * @property {Layout} layout
 * @property {Geometry} geometry
 * @property {Slots} slots
 * @property {{unit: object, title: object}} refs
 * @property {string} unitId the unit element's id, stable per tableId; the bulk bar's content region
 * @property {string} titleId
 * @property {string} captionId
 * @property {string} detailPrefix
 * @property {{onChange: Function, onKeyDown: Function, cancel: () => void}} search the search box's handlers
 * @property {() => void} retry repeats the last request (client: the consumer's onRetry) and records its
 *   initiator
 * @property {() => void} clearQuery clears search and every filter in one commit; the title takes focus, at
 *   once for a client source and once the request settles for a page one
 * @property {(state?: TableState|PublishedState) => object[]} exportRows the complete matching set as plain
 *   export values; empty for a page source
 * @property {(action: object) => {blocked: boolean, reason: ?string}} rowActionState blocked while a selection
 *   is active or bulk work runs, for an action that conflicts with bulk work (selection/conflicts.js)
 * @property {{isSelected: (id: string, row: object) => boolean}} selection whether a row is in the current
 *   selection; an ineligible row never is
 * @property {?((row: object) => object[])} rowActions [{key, label, icon, onSelect, danger, disabled}]
 * @property {?((row: object) => *)} renderDetail
 * @property {?{load: (row: object) => (object[]|Promise<object[]>), noun?: string, emptyText?: string}} childRows
 *   expanded rows show these rows in the table's own columns instead of a detail (rows/ChildRows.js)
 * @property {Map<string, {status: string, rows: object[], pending?: Promise<object>}>} childCache each expanded
 *   row's child rows; a loading entry holds its in-flight load as pending
 * @property {?(PageAction[]|*)} headerActions the headerActions prop, else config.pageActions, else null
 * @property {*} consumerRegion the consumer's node between the header and the toolbar, or null
 * @property {?Initiator} initiator
 * @property {{isOpen: (group: object) => boolean, toggle: (group: object) => void, token: object}} [groups]
 *   a grouped instance's section expansion (grouped/useGroupExpansion.js)
 */
